import { Dispatch, UnknownAction } from '@reduxjs/toolkit';

import {
  ConversationWithRelations,
  MessageWithRelations,
} from '../redux/ellipsisApi';
import { enhancedApi } from '../redux/enhancedApi';
import { postMessage } from '../redux/reducers/chatSlice';

/**
 * Service for managing conversations and messages
 */
class ConversationService {
  private dispatch: Dispatch;
  private userId: string;

  /**
   * Creates a new ConversationService instance
   * @param dispatch Redux dispatch function
   * @param userId Current user ID
   */
  constructor(dispatch: Dispatch, userId: string) {
    this.dispatch = dispatch;
    this.userId = userId;
  }

  /**
   * Sends a new message in a conversation
   * @param conversationId The ID of the conversation
   * @param content The message content
   * @param attachments Optional attachments for the message
   * @returns A promise that resolves to the sent message
   */
  async sendMessage(
    conversationId: string,
    content: string,
    attachments: any[] = []
  ): Promise<MessageWithRelations | undefined> {
    try {
      // Create the message object
      const newMessage: MessageWithRelations = {
        content,
        conversationId,
        authoredByUserId: this.userId,
        sentDate: new Date().toISOString(),
        attachments,
        readReceipts: [
          { userId: this.userId, readDate: new Date().toISOString() },
        ],
      };

      const result = this.dispatch(
        postMessage(newMessage) as unknown as UnknownAction
      );

      return result.data as MessageWithRelations;
    } catch (error) {
      console.error(
        `Error sending message to conversation ${conversationId}:`,
        error
      );
      return undefined;
    }
  }

  /**
   * Marks a message as read
   * @param messageId The ID of the message to mark as read
   * @returns A promise that resolves to the updated message
   */
  async markMessageAsRead(
    messageId: string
  ): Promise<MessageWithRelations | undefined> {
    try {
      // Send the update to the server using the updateReadReceipt API
      const result = this.dispatch(
        enhancedApi.endpoints.patchMessagesByIdReadReceipt.initiate({
          id: messageId,
        }) as unknown as UnknownAction
      );

      return result.data as MessageWithRelations;
    } catch (error) {
      console.error(`Error marking message ${messageId} as read:`, error);
      return undefined;
    }
  }

  /**
   * Deterministically locate an existing entry in the messages array that
   * corresponds to `incoming`. Matching priority:
   *   1. tempId echoed on either side (stable across optimistic -> confirmed).
   *   2. Same id (including when id is a tempId).
   *   3. Pending optimistic message from the same author with identical
   *      content (last-resort heuristic when the server does not echo tempId).
   */
  private findMessageIndex(
    messages: MessageWithRelations[],
    incoming: MessageWithRelations
  ): number {
    const incomingTempId = (incoming as any).tempId as string | undefined;

    if (incomingTempId) {
      const byTemp = messages.findIndex(
        (m) => (m as any).tempId === incomingTempId || m.id === incomingTempId
      );
      if (byTemp !== -1) return byTemp;
    }

    if (incoming.id) {
      const byId = messages.findIndex((m) => m.id === incoming.id);
      if (byId !== -1) return byId;

      const byExistingTempId = messages.findIndex(
        (m) => (m as any).tempId && (m as any).tempId === incoming.id
      );
      if (byExistingTempId !== -1) return byExistingTempId;
    }

    // Last-resort: pending optimistic entry with same author + content
    return messages.findIndex(
      (m) =>
        !!m.pending &&
        m.authoredByUserId === incoming.authoredByUserId &&
        m.content === incoming.content
    );
  }

  /**
   * Merge incoming message with any existing entry so we preserve the
   * client-side tempId (used as a stable list key) and never regress to a
   * pending state once the server has confirmed the message.
   */
  private mergeMessage(
    existing: MessageWithRelations | undefined,
    incoming: MessageWithRelations
  ): MessageWithRelations {
    const existingTempId = existing ? (existing as any).tempId : undefined;
    const incomingTempId = (incoming as any).tempId;
    const tempId = incomingTempId ?? existingTempId;
    return {
      ...incoming,
      ...(tempId ? { tempId } : {}),
      pending: false as any,
    } as MessageWithRelations;
  }

  /**
   * Processes a new message received via socket or REST response, reconciling
   * it with any optimistic entry already in the cache.
   * @param messageData The message data received
   */
  processNewMessage(messageData: MessageWithRelations): void {
    if (!messageData.conversationId) return;
    this.dispatch(
      enhancedApi.util.updateQueryData(
        'getConversationsById',
        { id: messageData.conversationId },
        (draft: ConversationWithRelations) => {
          // Update or insert the message in the detail cache
          const messages = draft.messages ?? [];
          const idx = this.findMessageIndex(messages, messageData);

          if (idx === -1) {
            messages.push(messageData);
          } else {
            // Replace the optimistic message with the server message, preserving tempId
            messages[idx] = this.mergeMessage(messages[idx], messageData);
          }

          // Deduplicate by ID and sort by sentDate to maintain order and prevent duplicate keys
          const uniqueMessages = Array.from(
            new Map(messages.map((m) => [m.id, m])).values()
          );
          draft.messages = uniqueMessages.sort(
            (a, b) =>
              new Date(a.sentDate).getTime() - new Date(b.sentDate).getTime()
          );
        }
      ) as unknown as UnknownAction
    );
    this.dispatch(
      enhancedApi.util.updateQueryData(
        'getConversations',
        { archived: 'false' },
        (draft) => {
          let conversationFound = false;
          draft.pages.map((page) => {
            page.conversations?.map((c: ConversationWithRelations) => {
              if (c.id === messageData.conversationId) {
                conversationFound = true;
                const messages = c.messages ?? [];
                const idx = this.findMessageIndex(messages, messageData);

                if (idx === -1) {
                  messages.push(messageData);
                } else {
                  messages[idx] = this.mergeMessage(messages[idx], messageData);
                }

                // Deduplicate by ID and sort by sentDate to maintain order
                const uniqueMessages = Array.from(
                  new Map(messages.map((m) => [m.id, m])).values()
                );
                c.messages = uniqueMessages.sort(
                  (a, b) =>
                    new Date(a.sentDate).getTime() -
                    new Date(b.sentDate).getTime()
                );
              }
            });
          });
          if (!conversationFound) {
            this.dispatch(
              enhancedApi.util.invalidateTags(['ConversationList'])
            );
          }
        }
      ) as unknown as UnknownAction
    );
    this.dispatch(enhancedApi.util.invalidateTags(['ConversationList']));
  }
}

export default ConversationService;
